import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,

  Grid,
  IconButton,
  InputAdornment,
  Stack,
 
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Tooltip from "@mui/material/Tooltip";

import { Collapse } from "antd";

import styles from "./css/FreightSimulationPage.module.css";
import theme from "../../theme";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import {
  cargoTypes,
  cityOptions as defaultCityOptions,
  useFreightSimulationActions,
  vehicleOptions,
} from "./action/action";
import Utils from "../../util/utils";

import { useState, useRef, useEffect } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useSelector } from "react-redux";

import IntlCurrencyInput from "react-intl-currency-input";
import { cardIconSx } from "../../theme/globalStyle/globalStyle";

require("firebase/auth");

export default function FreightSimulationPage() {
  const user = useSelector((state) => state.user);

  const {
    form,
    result,
    formError,
    hasCalculated,
    isRecalculateModalOpen,
    isCalculating,
    handleChange,
    handleCurrencyChange,
    handleOriginChange,
    handleCreateFreight,
    handleAddDestination,
    handleRemoveDestination,
    handleDestinationChange,
    handleDragEnd,
    handleReset,
    handleCalculate,
    handleCloseRecalculateModal,
    handleCloseFormErrorModal,
    formatCurrency,
    formatPercent,
    handleClientOptions,
  } = useFreightSimulationActions(user?.uidShipper);

  const currencyConfig = {
    locale: "pt-BR",
    formats: {
      number: {
        BRL: {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };

  const weightConfig = {
    locale: "pt-BR",
    formats: {
      number: {
        BRL: {
          style: "decimal",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };

  const items = [
    {
      key: "1",
      label: "Detalhamento dos custos",
      children: [
        <ResultRow
          label="Preço ANTT"
          value={formatCurrency(result.precoANTT)}
          padding={result.precoANTT > 0 ? 0.5 : 1}
        />,

        <ResultRow
          label="Pedágio"
          value={formatCurrency(result.pedagio)}
          padding={result.pedagio > 0 ? 0.5 : 1}
        />,

        <ResultRow
          label="Griss / Advalorem"
          value={formatCurrency(result.griss)}
          padding={result.griss > 0 ? 0.5 : 1}
        />,

        <ResultRow
          label="Taxa Adm"
          value={formatPercent(result.taxaAdm)}
          padding={result.total > 0 ? 0.5 : 1}
        />,

        <ResultRow
          label="Taxa Giro"
          value={formatPercent(result.taxaGiro)}
          padding={result.total > 0 ? 0.5 : 1}
        />,

        <ResultRow
          label="Impostos"
          value={formatPercent(result.impostos)}
          noDivider
          padding={result.total > 0 ? 0.3 : 1}
        />,
      ],
      styles: {
        header: {
          background: "#F9FAFB",
          color: "#012442",
          fontWeight: 600,
          fontSize: "1.05rem",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
        },
        body: {
          background: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderTop: "none",
          borderRadius: "0 0 12px 12px",
        },
      },
    },
  ];

  useEffect(() => {
    const loadClientOptions = async () => {
      if (!user?.uidShipper) return;
      const options = await handleClientOptions(user.uidShipper);
      setClientRecords(Array.isArray(options) ? options : []);
    };

    loadClientOptions();
  }, [user?.uidShipper, handleClientOptions]);

  const [cityOptions, setCityOptions] = useState([]);
  const [clientRecords, setClientRecords] = useState([]);
  const [cityInput, setCityInput] = useState("");
  const extractCityUf = (value) => {
    const match = String(value || "").match(/^(.*?)[,-]\s*([A-Za-z]{2})$/);
    if (!match) return null;

    return {
      city: match[1].trim(),
      uf: match[2].trim().toUpperCase(),
    };
  };

  const buildCityOptions = (input) => {
    const raw = String(input || "").trim();
    if (!raw) return [];

    const parsed = extractCityUf(raw);
    const result =
      parsed?.city && parsed?.uf
        ? Utils.buscarMunicipioPorNomeEUF(parsed.city, parsed.uf)
        : Utils.buscarMunicipiosPorNome(raw);

    const uniqueNames = Array.from(
      new Set(
        (result || [])
          .map((item) => {
            const city = item?.xMunCarrega;
            const uf = item?.UF;
            if (!city) return null;
            return uf ? `${city} - ${uf}` : city;
          })
          .filter(Boolean),
      ),
    );

    return uniqueNames.slice(0, 30);
  };

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const formatClientAddress = (client) => {
    const address = client?.address || {};
    const street = String(address.street || "").trim();
    const number = String(address.number || "").trim();
    const city = String(address.city || "").trim();
    const state = String(address.state || "")
    const complement = String(address.complement || "").trim()
      

    if (!street || !number || !city || !state) return "";
    return `${complement}  , ${number} - ${city}, ${state}`;
  };

  const toAddressOption = (value, source, extra = {}) => ({
    key: `${source}-${value}`,
    label: value,
    value,
    source,
    ...extra,
  });

  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    return option?.label || option?.value || "";
  };

  const getOptionValue = (option) => {
    if (typeof option === "string") return option;
    return option?.value || option?.label || "";
  };

  const toTitleCase = (value) =>
    String(value || "")
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const splitAddressLines = (rawValue) => {
    const cleaned = String(rawValue || "").trim();
    if (!cleaned) return { line1: "", line2: "" };

    const [line1, ...rest] = cleaned.split(" - ");
    const line2 = rest
      .join(" - ")
      .replace(/,\s*Brasil$/i, "")
      .trim();
    return {
      line1: String(line1 || "").trim(),
      line2,
    };
  };

  const buildOptionVisual = (option) => {
    const source = option?.source || "default";
    const rawValue = getOptionValue(option);
    const { line1, line2 } = splitAddressLines(rawValue);

    if (source === "client") {
      const company = toTitleCase(option?.companyName || "");
      return {
        primary: company || line1 || rawValue,
        secondary: company ? line1 : line2,
        tertiary: company ? line2 : "",
      };
    }

    return {
      primary: line1 || rawValue,
      secondary: line2,
      tertiary: "",
    };
  };

  const renderRouteOption = (props, option) => {
    const { key, ...liProps } = props;
    const visual = buildOptionVisual(option);

    return (
      <Box component="li" key={key} {...liProps} sx={routeOptionItemSx}>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            className="route-option-primary"
            noWrap
            sx={routeOptionPrimarySx}
          >
            {visual.primary}
          </Typography>
          {visual.secondary ? (
            <Typography noWrap sx={routeOptionSecondarySx}>
              {visual.secondary}
            </Typography>
          ) : null}
          {visual.tertiary ? (
            <Typography noWrap sx={routeOptionTertiarySx}>
              {visual.tertiary}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    );
  };

  const buildClientOptions = (input) => {
    const query = normalizeText(input);
    if (!query) return [];

    return (clientRecords || [])
      .map((client) => {
        const addressValue = formatClientAddress(client);
        if (!addressValue) return null;

        const name = String(client?.name || "").trim();
        const fantasyName = String(client?.nameFantasy || "").trim();
        const searchBag = normalizeText(
          `${name} ${fantasyName} ${addressValue}`,
        );

        if (!searchBag.includes(query)) return null;

        return {
          key: `client-${client.id}`,
          label: `${name || fantasyName} - ${addressValue}`,
          value: addressValue,
          source: "client",
          clientId: client.id,
          companyName: name || fantasyName,
        };
      })
      .filter(Boolean)
      .slice(0, 20);
  };

  const mergeUniqueOptions = (options) => {
    const seen = new Set();

    return options.filter((option) => {
      const value = normalizeText(getOptionValue(option));
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  };

  const fallbackRouteOptions = defaultCityOptions.map((city) =>
    toAddressOption(city, "default"),
  );

  const cargoTypeOptions = cargoTypes.map((item) => ({
    label: item,
    value: item,
  }));

  const vehicleTypeOptions = vehicleOptions.map((item) => ({
    label: item.label,
    value: item.value,
    axles: item.axles,
  }));

  const selectedVehicleOption =
    vehicleTypeOptions.find((item) => item.value === form.vehicle) || null;

  const availableAxles = selectedVehicleOption
    ? [selectedVehicleOption.axles]
    : Array.from(new Set(vehicleOptions.map((item) => item.axles)));

  const axleOptions = availableAxles.map((item) => ({
    label: `${item} eixos`,
    value: String(item),
    apiValue: item === 11 ? "all" : String(item),
  }));

  const findSelectOption = (options, value) =>
    options.find((option) => String(option.value) === String(value)) || null;

  const createSelectChangeHandler = (field) => (_, selected) => {
    const nextValue = selected?.value ?? "";
    handleChange(field)({ target: { value: nextValue } });
  };

  const handleVehicleChange = (_, selected) => {
    const nextVehicle = selected?.value ?? "";
    const nextAxles = selected?.axles ? String(selected.axles) : "";

    handleChange("vehicle")({ target: { value: nextVehicle } });
    handleChange("axles")({ target: { value: nextAxles } });
  };

  const renderFieldOption = (props, option) => {
    const { key, ...liProps } = props;

    return (
      <Box component="li" key={key} {...liProps} sx={routeOptionItemSx}>
        <Stack spacing={0.15}>
          <Typography noWrap sx={routeOptionPrimarySx}>
            {option.label}
          </Typography>
          {/* {option.apiValue === "all" ? (
            <Typography noWrap sx={routeOptionSecondarySx}>
              Enviado para a API como all
            </Typography>
          ) : null} */}
        </Stack>
      </Box>
    );
  };

  // Autocomplete google maps
  const key = "AIzaSyAds1TwGzvflRvD8KHrbRHrnF3DvATFM1k";
  const { isLoaded: isPlacesLoaded } = useJsApiLoader({
    googleMapsApiKey: key,
    libraries: ["places"],
  });

  const placesServiceRef = useRef(null);

  useEffect(() => {
    if (isPlacesLoaded && window.google) {
      placesServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [isPlacesLoaded]);

  const searchCityOptions = (input) => {
    const raw = String(input || "").trim();
    if (!raw) return setCityOptions([]);

    const clientOpts = buildClientOptions(raw);
    const localOpts = buildCityOptions(raw).map((city) =>
      toAddressOption(city, "local"),
    );

    if (!placesServiceRef.current) {
      return setCityOptions(mergeUniqueOptions([...clientOpts, ...localOpts]));
    }

    placesServiceRef.current.getPlacePredictions(
      {
        input: raw,
        componentRestrictions: { country: "br" },
        language: "pt-BR",
        types: ["geocode"],
      },
      (predictions, status) => {
        const googleOpts =
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
            ? predictions.map((p) => toAddressOption(p.description, "google"))
            : [];

        setCityOptions(
          mergeUniqueOptions([
            ...clientOpts,
            ...googleOpts,
            ...localOpts,
          ]).slice(0, 30),
        );
      },
    );
  };

  // Autocomplete google maps

  return (
    <NewMiniDrawer
      divOpen={
        <Box className={styles.container}>
          <Box className={styles.header}>
            <Box>
              <Typography
                sx={{
                  color: theme.colors.textPrimary,
                  fontWeight: theme.typography.weightBold,
                  fontSize: "2rem",
                  lineHeight: 1.2,
                }}
              >
                Simulação de Frete
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: theme.colors.textSecondary,
                  fontSize: theme.typography.bodySize,
                  fontWeight: 600,
                }}
              >
                Preencha os dados da rota, da carga e do veículo para calcular
                os valores estimados.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ m: 0, width: "100%" }}>
            <Grid item xs={12} lg={8}>
              <Card
                className={styles.card}
                sx={{
                  borderRadius: `${theme.radius.large}px`,
                  background: theme.colors.surface,
                  boxShadow:
                    "0px 9px 20px rgba(0,14,51,0.16), 0px 3px 6px rgba(0,14,51,0.12)",
                  border: "1px solid rgba(1, 36, 66, 0.28)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Box sx={cardIconSx}>
                      <FmdGoodOutlinedIcon fontSize="small" />
                    </Box>

                    <Typography
                      sx={{
                        color: theme.colors.primary,
                        fontWeight: theme.typography.weightBold,
                        fontSize: "1.25rem",
                      }}
                    >
                      Dados da simulação
                    </Typography>
                  </Stack>

                  <Grid container spacing={2.5} sx={{ m: 0, width: "100%" }}>
                    <Grid item xs={12} md={6}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1, minHeight: 40 }}
                      >
                        <Tooltip
                          describeChild
                          title="Busque por endereço completo ou cliente"
                          placement="top"
                          arrow
                          disableInteractive
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: 14,
                                borderRadius: 2,
                                px: 1.5,
                                py: 1,
                                boxShadow: 2,
                                width: "auto !important",
                              },
                            },
                          }}
                        >
                          <Typography
                            className={styles.label}
                            sx={{ mb: 0, fontWeight: 700 }}
                          >
                            Origem
                          </Typography>
                        </Tooltip>
                      </Stack>

                      

                      <Autocomplete
                        freeSolo
                        options={
                          cityOptions.length
                            ? cityOptions
                            : fallbackRouteOptions
                        }
                        noOptionsText="Nenhuma opção encontrada"
                        getOptionLabel={getOptionLabel}
                        filterOptions={(options) => options}
                        isOptionEqualToValue={(option, value) =>
                          getOptionValue(option) === getOptionValue(value)
                        }
                        slotProps={routeAutocompleteSlotProps}
                        renderOption={renderRouteOption}
                        value={form.origin || null}
                        inputValue={cityInput}
                        onInputChange={(_, newInputValue) => {
                          const input = newInputValue || "";
                          setCityInput(input);
                          searchCityOptions(input);
                        }}
                        onChange={(_, selected, reason) => {
                          if (reason === "clear") {
                            setCityInput("");
                            handleOriginChange(_, "");
                            return;
                          }

                          const selectedValue = getOptionValue(selected) || "";
                          setCityInput(selectedValue);
                          handleOriginChange(_, selectedValue);
                        }}
                        sx={{ ...selectSx }}
                        renderInput={(params) => (
                          <TextField
                            sx={{ height: "1px" }}
                            {...params}
                            placeholder="Busque por endereço completo ou cliente"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1, minHeight: 40 }}
                      >
                        <Tooltip
                          describeChild
                          title="Arraste os destinos para alterar a ordem"
                          placement="top"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: 14,
                                borderRadius: 2,
                                px: 1.5,
                                py: 1,
                                boxShadow: 2,
                              },
                            },
                          }}
                        >
                          <Typography
                            className={styles.label}
                            sx={{ mb: 0, fontWeight: 700 }}
                          >
                            Destino
                          </Typography>
                        </Tooltip>

                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleAddDestination}
                          sx={outlinedOrangeButtonSx}
                        >
                          Adicionar destino
                        </Button>
                      </Stack>

                      <Stack spacing={1.25}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="destinations">
                            {(provided) => (
                              <Stack
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                spacing={1.25}
                              >
                                {form.destinations.map((destination, index) => (
                                  <Draggable
                                    key={`destination-${index}`}
                                    draggableId={`destination-${index}`}
                                    index={index}
                                  >
                                    {(draggableProvided) => (
                                      <Stack
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        {form.destinations.length > 1 && (
                                          <IconButton
                                            {...draggableProvided.dragHandleProps}
                                            aria-label={`Arrastar destino ${index + 1}`}
                                            sx={dragHandleButtonSx}
                                          >
                                            <DragIndicatorIcon fontSize="medium" />
                                          </IconButton>
                                        )}

                                        <Autocomplete
                                          fullWidth
                                          freeSolo
                                          options={
                                            cityOptions.length
                                              ? cityOptions
                                              : fallbackRouteOptions
                                          }
                                          noOptionsText="Nenhuma opção encontrada"
                                          getOptionLabel={getOptionLabel}
                                          filterOptions={(options) => options}
                                          isOptionEqualToValue={(
                                            option,
                                            value,
                                          ) =>
                                            getOptionValue(option) ===
                                            getOptionValue(value)
                                          }
                                          slotProps={routeAutocompleteSlotProps}
                                          renderOption={renderRouteOption}
                                          value={destination}
                                          sx={{ ...selectSx }}
                                          onInputChange={(_, newInputValue) => {
                                            const input = newInputValue || "";
                                            searchCityOptions(input);
                                          }}
                                          onChange={(_, selected) =>
                                            handleDestinationChange(
                                              index,
                                              getOptionValue(selected) || "",
                                            )
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              placeholder="Busque por endereço completo ou cliente"
                                            />
                                          )}
                                        />

                                        {form.destinations.length > 1 && (
                                          <IconButton
                                            onClick={() =>
                                              handleRemoveDestination(index)
                                            }
                                            sx={deleteButtonSx}
                                          >
                                            <DeleteOutlineIcon fontSize="medium" />
                                          </IconButton>
                                        )}
                                      </Stack>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </Stack>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </Stack>

                      <Typography className={styles.helperText}>
                        Podem ser adicionados múltiplos destinos.
                      </Typography>
                    </Grid>

                    

                    <Grid item xs={16} md={4} sx={{ mt: 6 }}>
                      <Typography
                        className={styles.label}
                        sx={{ fontWeight: 700 }}
                      >
                        Tipo da mercadoria
                      </Typography>
                      <Autocomplete
                        fullWidth
                        options={cargoTypeOptions}
                        value={findSelectOption(
                          cargoTypeOptions,
                          form.cargoType,
                        )}
                        onChange={createSelectChangeHandler("cargoType")}
                        isOptionEqualToValue={(option, value) =>
                          String(option.value) === String(value?.value)
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        noOptionsText="Nenhuma opção encontrada"
                        slotProps={routeAutocompleteSlotProps}
                        renderOption={renderFieldOption}
                        sx={{ ...selectSx }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Selecione o tipo da carga"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ mt: 6 }}>
                      <Typography
                        className={styles.label}
                        sx={{ fontWeight: 700 }}
                      >
                        Valor da mercadoria
                      </Typography>
                      <IntlCurrencyInput
                        sx={{ ...selectSx }}
                        // disabled={view}
                        currency="BRL"
                        config={currencyConfig}
                        placeholder="00.00"
                        onChange={handleCurrencyChange("value")}
                        value={form.value}
                        className="form-control"
                        id="valueDriverFinal"
                        // Permite valores maiores (ex.: R$ 999.999.999,99)
                        maxLength={18}
                        style={{
                          borderRadius: "10px",
                        }}
                      />
                     
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ mt: 6 }}>
                      <Typography
                        className={styles.label}
                        sx={{ fontWeight: 700 }}
                      >
                        Peso da mercadoria
                      </Typography>

                      <IntlCurrencyInput
                        style={{
                          borderRadius: "10px",
                        }}
                        sx={{ ...selectSx }}
                        // disabled={view}
                        currency="BRL"
                        config={weightConfig}
                        placeholder="00.00"
                        onChange={handleCurrencyChange("weight")}
                        value={form.weight}
                        className="form-control"
                        id="weightCargo"
                        maxLength={10}
                        component={TextField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <ScaleOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">kg</InputAdornment>
                          ),
                        }}
                      />
                      
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography
                        className={styles.label}
                        sx={{ fontWeight: 700 }}
                      >
                        Veículo
                      </Typography>
                      <Autocomplete
                        fullWidth
                        options={vehicleTypeOptions}
                        value={findSelectOption(
                          vehicleTypeOptions,
                          form.vehicle,
                        )}
                        onChange={handleVehicleChange}
                        isOptionEqualToValue={(option, value) =>
                          String(option.value) === String(value?.value)
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        noOptionsText="Nenhuma opção encontrada"
                        slotProps={routeAutocompleteSlotProps}
                        renderOption={renderFieldOption}
                        sx={{ ...selectSx }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Selecione o veículo"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography
                        className={styles.label}
                        sx={{ fontWeight: 700 }}
                      >
                        Quantidade de Eixos
                      </Typography>
                      <Autocomplete
                        fullWidth
                        options={axleOptions}
                        value={findSelectOption(axleOptions, form.axles)}
                        onChange={createSelectChangeHandler("axles")}
                        isOptionEqualToValue={(option, value) =>
                          String(option.value) === String(value?.value)
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        noOptionsText="Nenhuma opção encontrada"
                        slotProps={routeAutocompleteSlotProps}
                        renderOption={renderFieldOption}
                        sx={{ ...selectSx }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Selecione a quantidade de eixos"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert
                        severity="warning"
                        sx={{
                          borderRadius: `${theme.radius.medium}px`,
                          background: theme.colors.highlightSoft,
                          color: theme.colors.textPrimary,
                          border: `1px solid rgba(255, 49, 0, 0.20)`,
                          "& .MuiAlert-icon": {
                            color: theme.colors.highlight,
                          },
                        }}
                      >
                        A simulação pode ter alteração de preço quando o
                        endereço não for digitado completamente, por exemplo,
                        quando for informado apenas Cidade/Estado.
                      </Alert>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        <Button
                          variant="contained"
                          startIcon={<CalculateOutlinedIcon />}
                          sx={primaryButtonSx}
                          onClick={handleCalculate}
                          disabled={isCalculating}
                        >
                          {isCalculating
                            ? "Calculando Simulação..."
                            : "Calcular Simulação"}
                        </Button>

                        <Button
                          variant="outlined"
                          startIcon={<RestartAltOutlinedIcon />}
                          sx={secondaryButtonSx}
                          onClick={handleReset}
                        >
                          Limpar
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Stack spacing={3}>
                <Card sx={resultCardSx}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={resultHeaderSx}
                    >
                      <Box sx={cardIconSx}>
                        <CurrencyExchangeIcon fontSize="small" />
                      </Box>

                      <Typography sx={resultTitleSx}>
                        Resultado da Simulação
                      </Typography>
                    </Stack>

                    {/* sx={{ fontWeight: 700}} */}
                    {!hasCalculated && (
                      <Typography
                        className={styles.helperText}
                        sx={{ fontSize: 12, fontWeight: 700 }}
                      >
                        Preencha os dados e clique em calcular para atualizar os
                        valores.
                      </Typography>
                    )}

                    <Collapse items={items} />

                    <Box sx={totalBoxSx}>
                      <Typography sx={totalLabelSx}>Total</Typography>
                      <Typography sx={totalValueSx}>
                        {formatCurrency(result.total)}
                      </Typography>
                    </Box>

                    {result.total > 0 && (
                      <Button sx={BTcreateFreight} onClick={handleCreateFreight}>
                        <Box
                          className="create-freight-icon"
                          sx={createFreightIconSx}
                        >
                          <LocalShippingOutlinedIcon fontSize="medium" />
                        </Box>

                        <Typography sx={createFreightSx}>
                          Criar Frete
                        </Typography>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          <Dialog
            open={isRecalculateModalOpen}
            onClose={handleCloseRecalculateModal}
            aria-labelledby="recalculate-simulation-dialog-title"
            aria-describedby="recalculate-simulation-dialog-description"
          >
            <DialogTitle id="recalculate-simulation-dialog-title">
              Simulação desatualizada
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="recalculate-simulation-dialog-description">
                Você alterou origem, destinos ou outros dados depois do último
                cálculo. Para criar o frete, é obrigatório refazer a simulação.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRecalculateModal} autoFocus>
                Entendi
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(formError)}
            onClose={handleCloseFormErrorModal}
            aria-labelledby="freight-simulation-error-dialog-title"
            aria-describedby="freight-simulation-error-dialog-description"
            PaperProps={{
              sx: errorDialogPaperSx,
            }}
          >
            <DialogTitle
              id="freight-simulation-error-dialog-title"
              sx={errorDialogTitleSx}
            >
              <Box sx={errorDialogIconSx}>
                <ErrorOutlineIcon fontSize="small" />
              </Box>
              Atenção
            </DialogTitle>
            <DialogContent sx={errorDialogContentSx}>
              <DialogContentText
                id="freight-simulation-error-dialog-description"
                sx={errorDialogTextSx}
              >
                {formError}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={errorDialogActionsSx}>
              <Button
                variant="contained"
                onClick={handleCloseFormErrorModal}
                autoFocus
                sx={errorDialogButtonSx}
              >
                Entendi
              </Button>
            </DialogActions>
          </Dialog>

          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
              flexDirection: "column",
              gap: 1.5,
            }}
            open={isCalculating}
          >
            <CircularProgress color="inherit" />
            <Typography sx={{ fontWeight: 700 }}>
              Calculando simulação...
            </Typography>
          </Backdrop>
        </Box>
      }
    />
  );
}

function ResultRow({ label, helper, value, noDivider = false, padding = 1 }) {
  return (
    <Box sx={{ py: padding }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box>
          <Typography sx={rowLabelSx}>{label}</Typography>
          {/* <Typography sx={rowHelperSx}>{helper}</Typography> */}
        </Box>

        <Typography sx={rowValueSx}>{value}</Typography>
      </Stack>

      {!noDivider && <Divider sx={{ mt: 1.5 }} />}
    </Box>
  );
}



const primaryButtonSx = {
  height: 52,
  px: 3,
  borderRadius: `${theme.radius.medium}px`,
  background: theme.colors.primary,
  color: theme.colors.white,
  boxShadow: "0 6px 14px rgba(1, 36, 66, 0.26)",
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  "& .MuiButton-startIcon": {
    color: "#FFFFFF",
    opacity: 1,
    "& svg": {
      fontSize: "1.15rem",
    },
  },
  "&:hover": {
    background: "#001a31",
    boxShadow: "0 9px 18px rgba(1, 36, 66, 0.34)",
  },
  "&:active": {
    transform: "translateY(1px)",
    boxShadow: "0 4px 10px rgba(1, 36, 66, 0.24)",
  },
};

const secondaryButtonSx = {
  height: 48,
  px: 2.5,
  borderRadius: `${theme.radius.medium}px`,
  borderColor: "rgba(1, 36, 66, 0.14)",
  color: "rgba(1, 36, 66, 0.82)",
  background: "rgba(1, 36, 66, 0.02)",
  textTransform: "none",
  fontWeight: theme.typography.weightRegular,
  "& .MuiButton-startIcon": {
    color: "rgba(1, 36, 66, 0.72)",
  },
  "&:hover": {
    borderColor: "rgba(1, 36, 66, 0.2)",
    background: "rgba(1, 36, 66, 0.05)",
  },
};

const outlinedOrangeButtonSx = {
  height: 42,
  px: 2,
  borderRadius: `${theme.radius.medium}px`,
  borderColor: "rgba(1, 36, 66, 0.22)",
  background: "rgba(1, 36, 66, 0.03)",
  color: theme.colors.primary,
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  boxShadow: "none",
  "& .MuiButton-startIcon": {
    color: theme.colors.primary,
  },
  "&:hover": {
    borderColor: "rgba(1, 36, 66, 0.34)",
    background: "rgba(1, 36, 66, 0.08)",
    boxShadow: "none",
  },
};

const deleteButtonSx = {
  
  color: theme.colors.highlight,
};

const dragHandleButtonSx = {
  
  color: theme.colors.primary,
 
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },
  "&:hover": {
    
    color: theme.colors.highlight,
  },
};


const resultCardSx = {
  borderRadius: `${theme.radius.large}px`,
  background: "linear-gradient(180deg, rgba(1, 36, 66, 0.04) 0%, #FFFFFF 18%)",
  boxShadow: "0px 9px 20px rgba(0,14,51,0.16), 0px 3px 6px rgba(0,14,51,0.12)",
  border: "1px solid rgba(1, 36, 66, 0.28)",
};

const errorDialogPaperSx = {
  width: "min(420px, calc(100% - 32px))",
  borderRadius: `${theme.radius.large}px`,
  background: theme.colors.surface,
  border: "1px solid rgba(255, 49, 0, 0.20)",
  boxShadow:
    "0px 18px 40px rgba(0,14,51,0.22), 0px 6px 14px rgba(0,14,51,0.14)",
  overflow: "hidden",
};

const errorDialogTitleSx = {
  px: 3,
  pt: 2.5,
  pb: 1.5,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  color: theme.colors.primary,
  fontWeight: theme.typography.weightBold,
  fontSize: "1.18rem",
};

const errorDialogIconSx = {
  width: 34,
  height: 34,
  borderRadius: `${theme.radius.medium}px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: theme.colors.highlight,
  background: theme.colors.highlightSoft,
  border: "1px solid rgba(255, 49, 0, 0.20)",
};

const errorDialogContentSx = {
  px: 3,
  pb: 1,
};

const errorDialogTextSx = {
  color: theme.colors.textPrimary,
  fontSize: "0.98rem",
  fontWeight: 500,
  lineHeight: 1.55,
};

const errorDialogActionsSx = {
  px: 3,
  pb: 2.5,
  pt: 1,
};

const errorDialogButtonSx = {
  minWidth: 112,
  height: 42,
  borderRadius: `${theme.radius.medium}px`,
  background: theme.colors.primary,
  color: theme.colors.white,
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  boxShadow: "0 6px 14px rgba(1, 36, 66, 0.24)",
  "&:hover": {
    background: "#001a31",
    boxShadow: "0 9px 18px rgba(1, 36, 66, 0.32)",
  },
};



const resultHeaderSx = {
  mb: 3,
  px: 1.25,
  py: 1,
  borderRadius: `${theme.radius.medium}px`,
  background: "rgba(1, 36, 66, 0.08)",
  border: "1px solid rgba(1, 36, 66, 0.14)",
};

const resultTitleSx = {
  color: theme.colors.primary,
  fontWeight: theme.typography.weightBold,
  fontSize: "1.3rem",
};

const rowLabelSx = {
  color: theme.colors.textPrimary,
  fontWeight: theme.typography.weightSemibold,
  fontSize: "1rem",
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${theme.radius.medium}px`,
    fontSize: 15, // tamanho do texto no input
    height: "40px",
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
};

const routeAutocompleteSlotProps = {
  paper: {
    sx: {
      mt: 1,
      borderRadius: "16px",
      border: "1px solid rgba(1, 36, 66, 0.16)",
      boxShadow: "0 14px 28px rgba(1, 36, 66, 0.18)",
      overflow: "hidden",
    },
  },
  listbox: {
    sx: {
      p: 0.75,
      maxHeight: 360,
      "& .MuiAutocomplete-option": {
        p: 0,
      },
      "&::-webkit-scrollbar": {
        width: 10,
      },
      "&::-webkit-scrollbar-track": {
        background: "rgba(1, 36, 66, 0.08)",
        borderRadius: 999,
      },
      "&::-webkit-scrollbar-thumb": {
        background:
          "linear-gradient(180deg, rgba(1, 36, 66, 0.48) 0%, rgba(1, 36, 66, 0.28) 100%)",
        borderRadius: 999,
        border: "2px solid rgba(1, 36, 66, 0.08)",
      },
    },
  },
};

const routeOptionItemSx = {
  borderRadius: "12px",
  px: 1.25,
  py: 0.9,
  mb: 0.45,
  alignItems: "stretch",
  border: "1px solid transparent",
  transition: "all 120ms ease",
  "&:last-of-type": {
    mb: 0,
  },
  "&:hover": {
    backgroundColor: "rgba(1, 36, 66, 0.06)",
    borderColor: "rgba(1, 36, 66, 0.12)",
  },
  '&[aria-selected="true"]': {
    backgroundColor: "rgba(1, 36, 66, 0.10)",
    borderColor: "rgba(1, 36, 66, 0.26)",
  },
  '&[aria-selected="true"] .route-option-primary': {
    color: "#001f3f",
  },
};

const routeOptionPrimarySx = {
  color: "#0f172a",
  fontSize: "0.92rem",
  fontWeight: 600,
  maxWidth: 330,
  lineHeight: 1.25,
};

const routeOptionSecondarySx = {
  color: "rgba(15, 23, 42, 0.78)",
  fontSize: "0.82rem",
  fontWeight: 500,
  lineHeight: 1.2,
  maxWidth: 440,
};

const routeOptionTertiarySx = {
  color: "rgba(1, 36, 66, 0.82)",
  fontSize: "0.78rem",
  fontWeight: 600,
  lineHeight: 1.2,
};

const rowValueSx = {
  color: theme.colors.textPrimary,
  fontWeight: theme.typography.weightBold,
  fontSize: "1rem",
  whiteSpace: "nowrap",
  height: 24,
};

const totalBoxSx = {
  mt: 2,
  p: 2,
  borderRadius: `${theme.radius.medium}px`,
  background: "linear-gradient(135deg, #012442 0%, #02345f 100%)",
  border: "1px solid rgba(1, 36, 66, 0.42)",
  boxShadow: "0px 6px 14px rgba(1, 36, 66, 0.20)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  height: 60,
};

const BTcreateFreight = {
  width: "100%",
  mt: 2,
  px: 3.25,
  py: 1.6,
  minHeight: 62,
  borderRadius: `${theme.radius.medium}px`,
  background:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 248, 252, 0.98) 100%)",
  color: theme.colors.primary,
  border: "1px solid rgba(1, 36, 66, 0.22)",
  boxShadow: "0 8px 18px rgba(1, 36, 66, 0.18)",
  textTransform: "none",
  fontWeight: theme.typography.weightBold,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.5,
  transition: theme.motion.transition,
  "&:hover": {
    background:
      "linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(241, 247, 252, 1) 100%)",
    borderColor: "rgba(1, 36, 66, 0.38)",
    boxShadow: "0 12px 24px rgba(1, 36, 66, 0.22)",
    transform: "translateY(-1px)",
    "& .create-freight-icon": {
      transform: "translateY(-1px) scale(1.03)",
      boxShadow: "0 8px 16px rgba(255, 49, 0, 0.40)",
    },
  },
  "&:active": {
    transform: "translateY(1px)",
    boxShadow: "0 6px 14px rgba(1, 36, 66, 0.2)",
  },
  "&:focus-visible": {
    outline: "3px solid rgba(255, 49, 0, 0.22)",
    outlineOffset: "2px",
  },
};

const createFreightSx = {
  fontSize: "clamp(1.65rem, 2.3vw, 2rem)",
  lineHeight: 1,

  letterSpacing: "0.02em",
  color: theme.colors.primary,
  fontWeight: theme.typography.weightBold,
};

const createFreightIconSx = {
  width: 36,
  height: 36,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: theme.colors.white,
  background: "linear-gradient(135deg, #ff4f21 0%, #ff3100 100%)",
 
  transition: theme.motion.transition,
  "& svg": {
    fontSize: "1.2rem",
  },
};

const totalLabelSx = {
  color: "rgba(255, 255, 255, 0.82)",
  fontWeight: theme.typography.weightBold,
  fontSize: "1rem",
};

const totalValueSx = {
  color: "#FFFFFF",
  fontWeight: theme.typography.weightBold,
  fontSize: "2.35rem",
  lineHeight: 1.1,
};


